import { useState, useMemo, useEffect } from 'react';
import { useAvaliacoes } from '../../api/hooks/useAvaliacoes';
import type { TipoAvaliacao, StatusAvaliacao, Avaliacao } from '../../api/hooks/useAvaliacoes';
import { useProfessorTurmas } from '../../api/hooks/useProfessorTurmas';
import { useAlunosTurmaChamada } from '../../api/hooks/useAlunosTurmaChamada';
import { useAuthStore } from '../../store/authStore';
import { toast } from 'sonner';
import { STATUS_LABELS } from './avaliacoes-professor-shared';

export function useAvaliacoesProfessorPage() {
  const user = useAuthStore((s) => s.user);
  const escolaId = useAuthStore((s) => s.escolaId);

  const [turmaId, setTurmaId] = useState('');
  const [materiaId, setMateriaId] = useState('');
  const [bimestre, setBimestre] = useState<number>(0);
  const [showForm, setShowForm] = useState(false);
  const [filtrosAbertos, setFiltrosAbertos] = useState(false);
  const [detalheId, setDetalheId] = useState<string | null>(null);
  const [avaliacaoNotas, setAvaliacaoNotas] = useState<Avaliacao | null>(null);
  const [notasGrid, setNotasGrid] = useState<Record<string, { valor: string; obs: string; faltou: boolean }>>({});

  const { turmas, todosHorarios, isLoading: loadingGrade } = useProfessorTurmas(user?.id);

  const minhasTurmas = useMemo(
    () => turmas.map((t) => ({ id: t.id, nome: t.nome })),
    [turmas],
  );

  const minhasMaterias = useMemo(() => {
    const map = new Map<string, string>();
    todosHorarios.forEach((h) => {
      if (h.materiaId && h.materia?.nome) map.set(h.materiaId, h.materia.nome);
    });
    return Array.from(map.entries())
      .map(([id, nome]) => ({ id, nome }))
      .sort((a, b) => a.nome.localeCompare(b.nome, 'pt-BR'));
  }, [todosHorarios]);

  const { avaliacoes, isLoading, criar, mudarStatus, lancarNotas, useNotasAvaliacao, useHistoricoNotas } =
    useAvaliacoes({
      turmaId: turmaId || undefined,
      materiaId: materiaId || undefined,
      professorId: user?.id,
      bimestre: bimestre || undefined,
      anoLetivo: new Date().getFullYear(),
    });

  const { alunos: alunosDaTurma, isLoading: loadingAlunosTurma } = useAlunosTurmaChamada(
    avaliacaoNotas?.turmaId,
  );
  const notasQuery = useNotasAvaliacao(avaliacaoNotas?.id);
  const historicoNotasQuery = useHistoricoNotas(detalheId ?? undefined);

  const filtrosAtivos = !!(turmaId || materiaId || bimestre);

  const avaliacaoDetalhe = useMemo(
    () => (detalheId ? avaliacoes.find((a) => a.id === detalheId) ?? null : null),
    [detalheId, avaliacoes],
  );

  useEffect(() => {
    if (!avaliacaoNotas || !notasQuery.data) return;
    const grid: Record<string, { valor: string; obs: string; faltou: boolean }> = {};
    notasQuery.data.forEach((n) => {
      grid[n.alunoId] = {
        valor: n.valorObtido != null ? String(n.valorObtido) : '',
        obs: n.observacao || '',
        faltou: !!n.faltou,
      };
    });
    setNotasGrid(grid);
  }, [avaliacaoNotas?.id, notasQuery.data]);

  const abrirLancarNotas = (av: Avaliacao) => {
    setDetalheId(null);
    setAvaliacaoNotas(av);
    setNotasGrid({});
  };

  const handleLancarNotas = () => {
    if (!avaliacaoNotas || !user?.id) {
      toast.error('Sessao invalida. Faca login novamente.');
      return;
    }
    const notas = alunosDaTurma.map((a) => {
      const g = notasGrid[a.id] || { valor: '', obs: '', faltou: false };
      return {
        alunoId: a.id,
        valorObtido: g.valor ? parseFloat(g.valor) : undefined,
        observacao: g.obs || undefined,
        entregou: !g.faltou,
        faltou: g.faltou,
      };
    });

    lancarNotas.mutate(
      { avaliacaoId: avaliacaoNotas.id, professorId: user.id, notas },
      {
        onSuccess: () => {
          toast.success(`Notas lancadas para ${notas.length} alunos!`);
          setAvaliacaoNotas(null);
          setNotasGrid({});
        },
        onError: () => toast.error('Erro ao lancar notas'),
      },
    );
  };

  const [formData, setFormData] = useState({
    titulo: '',
    tipo: 'PROVA' as TipoAvaliacao,
    turmaId: '',
    materiaId: '',
    dataAplicacao: '',
    peso: 1.0,
    valorMaximo: 10.0,
    bimestre: 1,
    descricao: '',
  });

  const handleCriar = () => {
    if (!formData.titulo.trim() || !formData.turmaId || !formData.materiaId) return;
    if (!escolaId || !user?.id) {
      toast.error('Sessao invalida. Faca login novamente.');
      return;
    }
    criar.mutate(
      {
        escolaId,
        turmaId: formData.turmaId,
        materiaId: formData.materiaId,
        professorId: user.id,
        titulo: formData.titulo.trim(),
        descricao: formData.descricao.trim() || undefined,
        tipo: formData.tipo,
        dataAplicacao: formData.dataAplicacao || undefined,
        peso: formData.peso,
        valorMaximo: formData.valorMaximo,
        bimestre: formData.bimestre,
        anoLetivo: new Date().getFullYear(),
      },
      {
        onSuccess: () => {
          toast.success('Avaliacao criada com sucesso!');
          setShowForm(false);
          setFormData({
            titulo: '',
            tipo: 'PROVA',
            turmaId: '',
            materiaId: '',
            dataAplicacao: '',
            peso: 1.0,
            valorMaximo: 10.0,
            bimestre: 1,
            descricao: '',
          });
        },
        onError: () => toast.error('Erro ao criar avaliacao'),
      },
    );
  };

  const handleMudarStatus = (id: string, novoStatus: StatusAvaliacao) => {
    mudarStatus.mutate(
      { id, status: novoStatus },
      {
        onSuccess: () => toast.success(`Status: ${STATUS_LABELS[novoStatus].label}`),
        onError: () => toast.error('Erro ao alterar status'),
      },
    );
  };

  const stats = useMemo(() => {
    const total = avaliacoes.length;
    const concluidas = avaliacoes.filter((a) => a.status === 'CONCLUIDA').length;
    const pendentes = avaliacoes.filter(
      (a) => a.status === 'RASCUNHO' || a.status === 'AGENDADA' || a.status === 'EM_APLICACAO',
    ).length;
    const porTipo = (Object.keys({ PROVA: 1, TRABALHO: 1, ATIVIDADE: 1, PARTICIPACAO: 1, RECUPERACAO: 1 }) as TipoAvaliacao[]).map(
      (tipo) => ({
        tipo,
        count: avaliacoes.filter((a) => a.tipo === tipo).length,
      }),
    );
    return { total, concluidas, pendentes, porTipo };
  }, [avaliacoes]);

  const pageLoading = isLoading || loadingGrade;

  return {
    user,
    escolaId,
    turmaId,
    setTurmaId,
    materiaId,
    setMateriaId,
    bimestre,
    setBimestre,
    showForm,
    setShowForm,
    filtrosAbertos,
    setFiltrosAbertos,
    detalheId,
    setDetalheId,
    avaliacaoDetalhe,
    avaliacaoNotas,
    setAvaliacaoNotas,
    notasGrid,
    setNotasGrid,
    avaliacoes,
    isLoading: pageLoading,
    minhasTurmas,
    minhasMaterias,
    filtrosAtivos,
    stats,
    formData,
    setFormData,
    handleCriar,
    handleMudarStatus,
    handleLancarNotas,
    abrirLancarNotas,
    criar,
    mudarStatus,
    lancarNotas,
    alunosDaTurma,
    loadingAlunosTurma,
    notasQuery,
    historicoNotasQuery,
  };
}

export type AvaliacoesProfessorPageContext = ReturnType<typeof useAvaliacoesProfessorPage>;
